import pathToRegexp from 'path-to-regexp'
import React, { Children, type ComponentType, cloneElement, isValidElement, type ReactNode } from 'react'
import {
  BrowserRouter,
  createPath,
  unstable_HistoryRouter as HistoryRouter,
  MemoryRouter,
  type matchPath as matchPathV7,
  Navigate,
  type NavigateOptions,
  type Path,
  parsePath,
  Link as RouterLink,
  type LinkProps as RouterLinkProps,
  NavLink as RouterNavLink,
  type NavLinkProps as RouterNavLinkProps,
  resolvePath,
  type To,
  useLocation as useLocationV7,
  useNavigate,
  useNavigationType,
  useParams as useParamsV7
} from 'react-router-dom-v7'

export * from 'react-router-dom-v7'
export { BrowserRouter, MemoryRouter }

type LegacyPath = string | readonly string[]
type LegacyLocationDescriptor = To | (Partial<Path> & { state?: unknown })

export function useLocation<State = unknown>() {
  return useLocationV7() as ReturnType<typeof useLocationV7> & { state: State }
}

interface LegacyRouteProps {
  children?: ReactNode | ((props: LegacyRouteComponentProps) => ReactNode)
  component?: ComponentType<any>
  exact?: boolean
  path?: LegacyPath
  render?: (props: LegacyRouteComponentProps) => ReactNode
  sensitive?: boolean
  strict?: boolean
}

interface LegacyRouteComponentProps {
  history: ReturnType<typeof useHistory>
  location: ReturnType<typeof useLocationV7>
  match: ReturnType<typeof matchPath>
}

// history v4 resolved relative URLs like browser addresses (from the current
// directory). Router 7 otherwise resolves them from its route hierarchy.
const normalizeTo = (to: LegacyLocationDescriptor, pathname: string): To => {
  const path = typeof to === 'string' ? parsePath(to) : to
  const directory = pathname.slice(0, pathname.lastIndexOf('/') + 1)
  return resolvePath({ ...path, pathname: path.pathname || pathname }, directory)
}

const destinationState = (to: LegacyLocationDescriptor) =>
  typeof to === 'object' && 'state' in to ? to.state : undefined

const linkDestination = (to: LegacyLocationDescriptor, pathname: string): To => {
  const target = typeof to === 'string' ? to : to.pathname
  // Download-app links use an absolute URL inside a legacy location object.
  if (target && /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(target)) {
    return typeof to === 'string' ? to : createPath(to)
  }
  return normalizeTo(to, pathname)
}

export const useHistory = () => {
  const navigate = useNavigate()
  const location = useLocationV7()
  const action = useNavigationType()
  const current = React.useRef({ navigate, location, action })
  current.current = { navigate, location, action }

  return React.useMemo(
    () => ({
      get action() {
        return current.current.action
      },
      back: () => current.current.navigate(-1),
      block: () => () => {},
      createHref: (to: To) => (typeof to === 'string' ? to : createPath(to)),
      forward: () => current.current.navigate(1),
      go: (delta: number) => current.current.navigate(delta),
      goBack: () => current.current.navigate(-1),
      goForward: () => current.current.navigate(1),
      length: window.history.length,
      listen: () => () => {},
      get location() {
        return current.current.location
      },
      push: (to: LegacyLocationDescriptor, state?: unknown) => {
        const embeddedState = destinationState(to)
        current.current.navigate(normalizeTo(to, current.current.location.pathname), {
          state: embeddedState !== undefined ? embeddedState : state
        })
      },
      replace: (to: LegacyLocationDescriptor, state?: unknown) => {
        const embeddedState = destinationState(to)
        current.current.navigate(normalizeTo(to, current.current.location.pathname), {
          replace: true,
          state: embeddedState !== undefined ? embeddedState : state
        })
      }
    }),
    []
  )
}

const LegacyMatchContext = React.createContext<ReturnType<typeof getMatch> | undefined>(undefined)

const patternCache = new Map<string, { regexp: RegExp; keys: pathToRegexp.Key[] }>()

const getMatch = (pathname: string, path?: LegacyPath, exact = false, sensitive = false, strict = false) => {
  if (path === undefined)
    return {
      params: {},
      pathname,
      pathnameBase: pathname,
      pattern: { path: '*', end: exact },
      path: '',
      url: pathname,
      isExact: true
    }

  const paths = typeof path === 'string' ? [path] : path
  for (const candidate of paths) {
    // Use the same pattern grammar as Router 5; removing regex constraints can
    // select the wrong route, and v7 cannot match repeated or partial parameters.
    const cacheKey = JSON.stringify([candidate, exact, sensitive, strict])
    let compiled = patternCache.get(cacheKey)
    if (!compiled) {
      const keys: pathToRegexp.Key[] = []
      compiled = { regexp: pathToRegexp(candidate, keys, { end: exact, sensitive, strict }), keys }
      if (patternCache.size < 10000) patternCache.set(cacheKey, compiled)
    }
    const match = compiled.regexp.exec(pathname)
    if (!match) continue
    const url = candidate === '/' && match[0] === '' ? '/' : match[0]
    const params: Record<string, string | undefined> = {}
    compiled.keys.forEach((key, index) => {
      params[key.name] = match[index + 1]
    })
    // Keep the adapter's existing v7 splat alias alongside v5's numeric keys.
    const splat = compiled.keys.findIndex(key => key.asterisk)
    if (splat !== -1) params['*'] = match[splat + 1]
    return {
      params,
      pathname: url,
      pathnameBase: url,
      pattern: { path: candidate, end: exact, caseSensitive: sensitive },
      path: candidate,
      url,
      isExact: match[0] === pathname
    }
  }
  return null
}

export const matchPath = <Params extends string = string>(
  pathname: string,
  options: LegacyPath | { exact?: boolean; path?: LegacyPath; sensitive?: boolean; strict?: boolean } = {}
) => {
  const config =
    typeof options === 'string' || Array.isArray(options)
      ? { path: options as LegacyPath }
      : (options as { exact?: boolean; path?: LegacyPath; sensitive?: boolean; strict?: boolean })
  if (config.path === undefined) return null
  return getMatch(pathname, config.path, config.exact, config.sensitive, config.strict) as
    | (ReturnType<typeof matchPathV7<Params>> & { isExact?: boolean; path?: string; url?: string })
    | null
}

export const useRouteMatch = <Params extends object = Record<string, string | undefined>>(
  path?: string | { exact?: boolean; path?: LegacyPath; sensitive?: boolean; strict?: boolean }
) => {
  const location = useLocationV7()
  const routeMatch = React.useContext(LegacyMatchContext)
  const config = typeof path === 'string' ? { path } : (path ?? {})
  return (path === undefined
    ? routeMatch === undefined
      ? getMatch(location.pathname, '/', false)
      : routeMatch
    : getMatch(location.pathname, config.path, config.exact, config.sensitive, config.strict)) as unknown as
    | (ReturnType<typeof matchPathV7> & { params: Params })
    | null
}

export const useParams = <Params extends string | Record<string, string | undefined> = string>() => {
  const match = React.useContext(LegacyMatchContext)
  const params = useParamsV7<Params>()
  return (match === undefined ? params : (match?.params ?? {})) as ReturnType<typeof useParamsV7<Params>>
}

export const Route = ({ children, component: Component, exact, path, render, sensitive, strict }: LegacyRouteProps) => {
  const location = useLocationV7()
  const history = useHistory()
  const parentMatch = React.useContext(LegacyMatchContext)
  const match = path
    ? getMatch(location.pathname, path, exact, sensitive, strict)
    : (parentMatch ?? getMatch(location.pathname, '/', false))
  if (!match && typeof children !== 'function') return null

  const props = { history, location, match }
  const content = children ? (
    typeof children === 'function' ? (
      children(props)
    ) : (
      children
    )
  ) : Component ? (
    <Component {...props} />
  ) : render ? (
    render(props)
  ) : null
  return <LegacyMatchContext.Provider value={match}>{content}</LegacyMatchContext.Provider>
}

export const Switch = ({ children }: { children?: ReactNode }) => {
  const { pathname } = useLocationV7()
  const child = Children.toArray(children).find(candidate => {
    if (!isValidElement<LegacyRouteProps & { from?: LegacyPath }>(candidate)) return false
    const { exact, path, sensitive, strict } = candidate.props
    return Boolean(getMatch(pathname, path ?? candidate.props.from, exact, sensitive, strict))
  })
  return child && isValidElement(child) ? cloneElement(child) : null
}

export const Redirect = ({
  push = false,
  to
}: {
  push?: boolean
  to: LegacyLocationDescriptor
  from?: LegacyPath
  exact?: boolean
  sensitive?: boolean
  strict?: boolean
}) => {
  const { pathname } = useLocationV7()
  return <Navigate replace={!push} to={normalizeTo(to, pathname)} state={destinationState(to)} />
}

interface LegacyNavLinkProps extends Omit<RouterNavLinkProps, 'className' | 'to'> {
  activeClassName?: string
  className?: string
  exact?: boolean
  to: LegacyLocationDescriptor
}

export const NavLink = ({ activeClassName = 'active', className, exact, to, state, ...props }: LegacyNavLinkProps) => {
  const { pathname } = useLocationV7()
  return (
    <RouterNavLink
      {...props}
      to={linkDestination(to, pathname)}
      state={state !== undefined ? state : destinationState(to)}
      end={exact}
      className={({ isActive }) => [className, isActive && activeClassName].filter(Boolean).join(' ')}
    />
  )
}

export interface LinkProps extends Omit<RouterLinkProps, 'to'> {
  to: LegacyLocationDescriptor
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(({ to, state, ...props }, ref) => {
  const { pathname } = useLocationV7()
  return (
    <RouterLink
      {...props}
      ref={ref}
      to={linkDestination(to, pathname)}
      state={state !== undefined ? state : destinationState(to)}
    />
  )
})

// React Router 5's low-level Router accepted a history object. This adapter is
// intentionally kept for the few legacy tests and library components that still
// pass one; production uses BrowserRouter.
export const Router = ({ children, history }: { children?: ReactNode; history?: unknown }) =>
  history ? <HistoryRouter history={history as any}>{children}</HistoryRouter> : <MemoryRouter>{children}</MemoryRouter>

export type { NavigateOptions }
